import { DOCUMENT_LIMITS } from './document';
import { checkRichText, hasOnlyKeys } from './rich-text';
/**
 * Hand written blocks of a chapter as the editor sends them (design
 * 2026-10-31, C1). Order is the array order; stamps and visibility are added
 * by the API.
 */
export const SECTION_BLOCK_TYPES = ['richText', 'image', 'note', 'warning'];
/** Full width, half, third. */
export const IMAGE_WIDTHS = [100, 50, 33];
const BLOCK_ID_PATTERN = /^blk_[A-Za-z0-9_-]{1,64}$/;
const ASSET_ID_PATTERN = /^ast_[A-Za-z0-9_-]{1,64}$/;
const INVALID_BLOCKS = 'Die Blöcke des Kapitels sind ungültig.';
/** Validates the block list of a section write. Unknown fields are refused, not dropped. */
export function readSectionBlockInputs(value) {
    if (!Array.isArray(value))
        return { error: INVALID_BLOCKS };
    const blocks = [];
    const ids = new Set();
    for (const raw of value) {
        if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
            return { error: INVALID_BLOCKS };
        const input = raw;
        if (typeof input.id !== 'string' || !BLOCK_ID_PATTERN.test(input.id))
            return { error: INVALID_BLOCKS };
        if (ids.has(input.id))
            return { error: 'Ein Block kommt doppelt vor.' };
        ids.add(input.id);
        if (input.type === 'richText' || input.type === 'note' || input.type === 'warning') {
            if (!hasOnlyKeys(input, ['id', 'type', 'content']))
                return { error: INVALID_BLOCKS };
            const problem = checkRichText(input.content);
            if (problem)
                return { error: problem };
            blocks.push({ id: input.id, type: input.type, content: input.content });
            continue;
        }
        if (input.type !== 'image')
            return { error: INVALID_BLOCKS };
        if (!hasOnlyKeys(input, ['id', 'type', 'assetId', 'alternativeText', 'caption', 'widthPercent'])) {
            return { error: INVALID_BLOCKS };
        }
        if (typeof input.assetId !== 'string' || !ASSET_ID_PATTERN.test(input.assetId)) {
            return { error: INVALID_BLOCKS };
        }
        if (typeof input.alternativeText !== 'string')
            return { error: INVALID_BLOCKS };
        if (input.caption !== undefined && typeof input.caption !== 'string')
            return { error: INVALID_BLOCKS };
        if (!IMAGE_WIDTHS.includes(input.widthPercent)) {
            return { error: 'Die Bildbreite muss voll, ½ oder ⅓ sein.' };
        }
        const alternativeText = input.alternativeText.trim();
        const caption = typeof input.caption === 'string' ? input.caption.trim() : '';
        if (alternativeText.length > DOCUMENT_LIMITS.maxAlternativeTextLength) {
            return { error: 'Der Alternativtext ist zu lang (höchstens 300 Zeichen).' };
        }
        if (caption.length > DOCUMENT_LIMITS.maxCaptionLength) {
            return { error: 'Die Bildunterschrift ist zu lang (höchstens 300 Zeichen).' };
        }
        blocks.push({
            id: input.id,
            type: 'image',
            assetId: input.assetId,
            alternativeText,
            ...(caption ? { caption } : {}),
            widthPercent: input.widthPercent,
        });
    }
    return { blocks };
}
/** Stored block -> editor input; `undefined` for block types C1 does not edit. */
export function toSectionBlockInput(block) {
    switch (block.type) {
        case 'richText':
        case 'note':
        case 'warning':
            return { id: block.id, type: block.type, content: block.content };
        case 'image': {
            const width = block.layout?.widthPercent ?? 100;
            return {
                id: block.id,
                type: 'image',
                assetId: block.asset.assetId,
                alternativeText: block.asset.alternativeText ?? '',
                ...(block.caption ? { caption: block.caption } : {}),
                widthPercent: IMAGE_WIDTHS.includes(width)
                    ? width
                    : 100,
            };
        }
        default:
            return undefined;
    }
}
/** Every asset the image blocks reference, each once. */
export function blockAssetIds(blocks) {
    return [...new Set(blocks.flatMap((block) => (block.type === 'image' ? [block.assetId] : [])))];
}
//# sourceMappingURL=section-blocks.js.map